/**
 * SBOT · comportamento dos extras da gaveta (transplante tema-ref -> Horizon).
 *
 * Quem desenha é o Liquid (snippets/sbot-cart-extras.liquid). Este módulo só cuida do que o
 * Liquid não faz:
 *   - <sbot-upsell-component>: adiciona/remove o produto de upsell pelo MESMO contrato do
 *     product-form do Horizon (POST /cart/add.js pedindo a seção cart-drawer-section + o
 *     CartLinesUpdateEvent de @shopify/events). O cart-items-component do Horizon recebe o
 *     evento e re-renderiza a gaveta inteira (itens, barra, upsell) com o morph dele.
 *   - <sbot-tnc-component> + trava do checkout: sem o aceite, o clique em "checkout" é barrado
 *     e o aviso aparece. O aceite sobrevive aos re-renders (input com data-skip-node-update +
 *     estado em memória) e não vem pré-marcado em outra página.
 *   - <sbot-countdown-component>: prazo guardado na sessão — não reinicia a cada render.
 *   - SbotCart.refresh / sincronização: quando código de terceiros (molde KB, apps) muda o
 *     carrinho por /cart/add|change|update SEM pedir seções, a gaveta é atualizada e aberta.
 *   - Abertura da gaveta no editor de temas ao selecionar a seção/bloco.
 * (Gaveta "por cima" no desktop = horizon_transplante.parte_compat, MODAL_BREAKPOINT 99999.)
 */
import { Component } from '@theme/component';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'cart-drawer-section';
const DRAWER_ID = 'cart-drawer';
const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const TIMER_KEY = 'sbot:cart-timer-deadline';

/** @type {{ sync?: boolean, designMode?: boolean, timerLoop?: number, iconFont?: boolean }} */
const config = readConfig();

function readConfig() {
  const element = document.getElementById('sbot-cart-config');
  try {
    return JSON.parse(element?.textContent || '{}');
  } catch {
    return {};
  }
}

/** sessionStorage pode lançar exceção (aba anônima, bloqueio) — a gaveta continua funcionando sem ele. */
const session = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* sem persistência: o contador vive só nesta página */
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* idem */
    }
  },
};

/** `Theme` é um const global de snippets/scripts.liquid (não vira propriedade de window). */
function routes() {
  // eslint-disable-next-line no-undef
  const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : null;
  return {
    add: themeRoutes?.cart_add_url ?? '/cart/add.js',
    change: `${themeRoutes?.cart_change_url ?? '/cart/change'}.js`,
    cart: `${themeRoutes?.cart_url ?? '/cart'}.js`,
  };
}

/** @returns {(HTMLElement & { open?: () => void, close?: () => void, isOpen?: boolean }) | null} */
function drawer() {
  return /** @type {any} */ (document.getElementById(DRAWER_ID));
}

function openDrawer() {
  const element = drawer();
  if (element && !element.isOpen && typeof element.open === 'function') element.open();
}

function closeDrawer() {
  const element = drawer();
  if (element?.isOpen && typeof element.close === 'function') element.close();
}

/** Mesma regra do product-form do Horizon: toda seção de carrinho presente na página vem junto. */
function cartSectionIds() {
  const ids = new Set([SECTION_ID]);
  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

async function fetchCart() {
  const response = await fetch(routes().cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

/**
 * Anuncia a mudança do carrinho pelo evento padrão do Horizon. O contrato exige >= 1 linha.
 * @param {EventTarget} origin
 * @param {'add' | 'remove' | 'update'} action
 * @param {Array<Record<string, string | number>>} lines
 */
function announce(origin, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent(/** @type {any} */ ({ action, context: 'cart', lines, promise: deferred.promise }))
  );
  return deferred;
}

class SbotCartError extends Error {}

/**
 * Adiciona itens ao carrinho e deixa o Horizon re-renderizar a gaveta.
 * @param {Array<{ id: number | string, quantity?: number }>} items
 * @param {{ origin?: EventTarget, source?: string }} [options]
 */
async function addToCart(items, { origin = document.body, source = 'sbot' } = {}) {
  const payload = items.map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) || 1 }));
  const deferred = announce(
    origin,
    'add',
    payload.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }))
  );

  try {
    const response = await fetch(routes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: payload, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    const cart = await fetchCart();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Add to cart failed';
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, itemCount: cart.item_count, source },
      });
      origin.dispatchEvent(new CartErrorEvent(/** @type {any} */ ({ error: message, code: 'INVALID' })));
      throw new SbotCartError(message);
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: cart.items, itemCount: cart.item_count, source, didError: false },
    });
    return cart;
  } catch (error) {
    deferred.reject(error); // sem efeito se já resolvido
    throw error;
  }
}

/**
 * Remove uma linha (upsell por alternância).
 * @param {string} lineKey
 * @param {{ origin?: EventTarget, source?: string }} [options]
 */
async function removeLine(lineKey, { origin = document.body, source = 'sbot' } = {}) {
  const deferred = announce(origin, 'remove', [{ id: lineKey, quantity: 0 }]);
  try {
    const response = await fetch(routes().change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new SbotCartError(data.description || data.message || data.errors || 'Cart change failed');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { sections: data.sections, items: data.items, itemCount: data.item_count, source, didError: false },
    });
    return data;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Atualiza a gaveta a partir do servidor. Contrato para o molde KB / apps (W1c):
 *   window.SbotCart.refresh({ open: true, lines: [{ merchandiseId: '123', quantity: 1 }] })
 * Com `lines`, dispara o evento padrão (gaveta + bolha do carrinho + leitores de tela);
 * sem `lines` (o evento exige >= 1), só re-renderiza a seção da gaveta.
 * @param {{ open?: boolean, lines?: Array<{ merchandiseId: string, quantity: number }>, origin?: EventTarget, source?: string }} [options]
 */
async function refresh({ open = false, lines = [], origin = document.body, source = 'sbot-refresh' } = {}) {
  if (lines.length) {
    const deferred = announce(origin, 'add', lines);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', cartSectionIds().join(','));
      const [sections, cart] = await Promise.all([fetch(url).then((response) => response.json()), fetchCart()]);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections, items: cart.items, itemCount: cart.item_count, source, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
    }
  } else {
    try {
      // injectStylesheet: a gaveta pode ter carregado vazia (sem o CSS do resumo/itens)
      await sectionRenderer.renderSection(SECTION_ID, { cache: false, mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      console.warn('[sbot-cart] refresh falhou:', error);
    }
  }
  if (open) openDrawer();
}

/* ------------------------------------------------------------------ upsell */

/**
 * Card de upsell. Estilo add_button: `on:click="/add"`; estilos de alternância: `on:click="/toggle"`.
 * @extends {Component}
 */
class SbotUpsell extends Component {
  #busy = false;
  /** @type {number | undefined} */
  #errorTimer;

  updatedCallback() {
    super.updatedCallback();
    this.#syncPrice(); // o morph devolve o preço do servidor; a variante escolhida fica no <select>
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#errorTimer);
  }

  /** @param {Event} [event] */
  async add(event) {
    event?.preventDefault?.();
    if (this.#busy) return;
    const variantId = this.#variantId();
    if (!variantId) return;
    this.#setBusy(true);
    try {
      await addToCart([{ id: variantId, quantity: 1 }], { origin: this, source: 'sbot-upsell' });
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {Event} [event] */
  async toggle(event) {
    event?.preventDefault?.();
    if (this.#busy) return;
    const lineKey = this.dataset.lineKey;
    this.#setBusy(true);
    try {
      if (this.dataset.selected === 'true' && lineKey) {
        await removeLine(lineKey, { origin: this, source: 'sbot-upsell' });
      } else {
        await addToCart([{ id: this.#variantId(), quantity: 1 }], { origin: this, source: 'sbot-upsell' });
      }
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  }

  selectVariant() {
    this.#syncPrice();
  }

  #select() {
    const select = this.querySelector('select.sbot-upsell__variant');
    return select instanceof HTMLSelectElement ? select : null;
  }

  #variantId() {
    return Number(this.#select()?.value || this.dataset.variantId) || 0;
  }

  #syncPrice() {
    const option = this.#select()?.selectedOptions[0];
    const price = this.querySelector('[data-sbot-price]');
    if (option?.dataset.price && price && price.textContent !== option.dataset.price) {
      price.textContent = option.dataset.price;
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    const button = this.querySelector('.sbot-upsell__add, .sbot-upsell__toggle');
    if (button instanceof HTMLButtonElement) button.disabled = busy;
  }

  /** @param {unknown} error */
  #showError(error) {
    const box = this.querySelector('.sbot-upsell__error');
    if (!(box instanceof HTMLElement)) return;
    box.textContent = error instanceof Error ? error.message : String(error);
    box.hidden = false;
    clearTimeout(this.#errorTimer);
    this.#errorTimer = window.setTimeout(() => {
      box.hidden = true;
    }, 5000);
  }
}

/* -------------------------------------------------------- termo de aceite */

/** Aceite só na memória da página: sobrevive aos re-renders do carrinho; outra página começa desmarcado. */
let tncAccepted = false;

/**
 * Checkbox de termos. O <input> tem data-skip-node-update, então o morph do Horizon não o desmarca.
 * @extends {Component}
 */
class SbotTnc extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  get accepted() {
    return Boolean(this.#input()?.checked);
  }

  onChange() {
    tncAccepted = this.accepted;
    if (tncAccepted) this.hideWarning();
  }

  showWarning() {
    const warning = this.#warning();
    if (warning) warning.hidden = false;
    this.#input()?.focus();
  }

  hideWarning() {
    const warning = this.#warning();
    if (warning) warning.hidden = true;
  }

  #input() {
    const input = this.querySelector('input[type="checkbox"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #warning() {
    return this.dataset.warningId ? document.getElementById(this.dataset.warningId) : null;
  }

  #sync() {
    const input = this.#input();
    if (!input) return;
    if (input.checked && !tncAccepted) {
      tncAccepted = true; // marcado antes de o módulo carregar (rede lenta): vale o que o cliente fez
    } else if (input.checked !== tncAccepted) {
      input.checked = tncAccepted; // nó recriado pelo re-render: reaplica o aceite desta página
    }
    if (tncAccepted) this.hideWarning();
  }
}

function blockingTnc() {
  const tnc = document.querySelector(`#${DRAWER_ID} sbot-tnc-component`);
  return tnc instanceof SbotTnc && !tnc.accepted ? tnc : null;
}

/**
 * Trava do checkout. Captura em window (antes de qualquer ouvinte do document) para o clique no
 * botão nativo do Horizon e para o envio do #cart-form (Enter num campo dispara o botão padrão).
 * @param {Event} event
 */
function gateCheckout(event) {
  if (event.type === 'click') {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(`#${DRAWER_ID} [name="checkout"]`)) return;
  } else {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest(`#${DRAWER_ID}`)) return;
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (submitter && submitter.getAttribute('name') !== 'checkout') return;
  }
  const tnc = blockingTnc();
  if (!tnc) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  tnc.showWarning();
}

/* ------------------------------------------------------------- contador */

/** @type {Set<SbotCountdown>} */
const timers = new Set();
let tickHandle = 0;

/** @param {number} duration segundos */
function ensureDeadline(duration) {
  if (!Number(session.get(TIMER_KEY))) session.set(TIMER_KEY, String(Date.now() + duration * 1000));
}

/** @returns {number | null} segundos restantes, ou null se o prazo ainda não começou */
function secondsLeft() {
  let deadline = Number(session.get(TIMER_KEY)) || 0;
  if (!deadline) return null;
  const now = Date.now();
  if (deadline <= now) {
    // tema-ref: ao zerar, o contador recomeça em 90 s (loop) em vez de travar em 00:00
    deadline = now + (Number(config.timerLoop) || 90) * 1000;
    session.set(TIMER_KEY, String(deadline));
  }
  return Math.ceil((deadline - now) / 1000);
}

function tick() {
  for (const timer of timers) timer.paint();
}

/**
 * Relógio "mm:ss". Começa na primeira abertura da gaveta (como o tema-ref) e segue o mesmo prazo
 * entre re-renders e páginas da sessão. O elemento tem data-skip-subtree-update: o morph não
 * sobrescreve os dígitos. HTMLElement simples — não usa refs nem eventos declarativos.
 */
class SbotCountdown extends HTMLElement {
  connectedCallback() {
    timers.add(this);
    if (drawer()?.isOpen) ensureDeadline(this.duration);
    this.paint();
    if (!tickHandle) tickHandle = window.setInterval(tick, 1000);
  }

  disconnectedCallback() {
    timers.delete(this);
    if (!timers.size && tickHandle) {
      clearInterval(tickHandle);
      tickHandle = 0;
    }
  }

  get duration() {
    return Number(this.dataset.duration) || 300;
  }

  paint() {
    const left = secondsLeft() ?? this.duration;
    const text = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    if (this.textContent !== text) this.textContent = text;
  }
}

/* ------------------------------------------------- sincronização externa */

const CART_MUTATION = /\/cart\/(add|change|update)(?:\.js)?(?:[?#]|$)/;

/**
 * Mudança de carrinho feita por terceiros que NÃO pediram seções (quem pede seções — product-form
 * do Horizon, cart-items, cart-discount, este módulo — já atualiza a gaveta sozinho).
 * @param {RequestInfo | URL} input
 * @param {RequestInit | undefined} init
 * @returns {string | null} add | change | update
 */
function foreignCartMutation(input, init) {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url ?? '';
  const match = CART_MUTATION.exec(url);
  if (!match) return null;
  if (/[?&]sections=/.test(url)) return null;
  const body = init?.body;
  if (typeof body === 'string' && /["']?sections["']?\s*[:=]/.test(body)) return null;
  if ((body instanceof FormData || body instanceof URLSearchParams) && body.has('sections')) return null;
  return match[1] ?? null;
}

/** @type {Array<{ merchandiseId: string, quantity: number }>} */
let pendingLines = [];
let pendingOpen = false;
let syncTimer = 0;

/**
 * @param {string} kind
 * @param {Response} response clone — a resposta original segue intacta para quem chamou
 */
async function queueSync(kind, response) {
  if (kind === 'add') {
    pendingOpen = true;
    try {
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : data?.variant_id ? [data] : [];
      for (const item of items) {
        pendingLines.push({ merchandiseId: String(item.variant_id), quantity: Number(item.quantity) || 1 });
      }
    } catch {
      /* resposta sem JSON: atualiza sem linhas */
    }
  }
  clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    const lines = pendingLines;
    const open = pendingOpen;
    pendingLines = [];
    pendingOpen = false;
    refresh({ open, lines, source: 'sbot-drawer-sync' });
  }, 120);
}

/**
 * Envolve window.fetch sem tocar nos argumentos nem no corpo (o molde KB já mostrou que um
 * wrapper que reescreve o pedido derruba o add em lote). Só lê um CLONE da resposta.
 */
function installFetchSync() {
  if (!config.sync || /** @type {any} */ (window).__sbotDrawerSync) return;
  /** @type {any} */ (window).__sbotDrawerSync = true;
  const nativeFetch = window.fetch;
  window.fetch = function sbotDrawerSyncFetch(input, init) {
    const result = nativeFetch.apply(window, /** @type {any} */ (arguments));
    let kind = null;
    try {
      kind = foreignCartMutation(input, init);
    } catch {
      kind = null;
    }
    if (!kind) return result;
    return result.then((response) => {
      if (response?.ok) queueSync(kind, response.clone());
      return response;
    });
  };
}

/* ---------------------------------------------------------------- boot */

if (!customElements.get('sbot-upsell-component')) customElements.define('sbot-upsell-component', SbotUpsell);
if (!customElements.get('sbot-tnc-component')) customElements.define('sbot-tnc-component', SbotTnc);
if (!customElements.get('sbot-countdown-component')) customElements.define('sbot-countdown-component', SbotCountdown);

if (!(/** @type {any} */ (window).__sbotCartExtras)) {
  /** @type {any} */ (window).__sbotCartExtras = true;

  window.addEventListener('click', gateCheckout, { capture: true });
  window.addEventListener('submit', gateCheckout, { capture: true });

  document.addEventListener(DRAWER_OPEN_EVENT, (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== DRAWER_ID) return;
    for (const timer of timers) {
      ensureDeadline(timer.duration);
      timer.paint();
    }
  });

  // Carrinho esvaziado: o próximo contador começa do prazo cheio.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => {
        if (result?.cart && result.cart.totalQuantity === 0) session.remove(TIMER_KEY);
      })
      .catch(() => {});
  });

  if (config.designMode) {
    for (const name of ['shopify:section:select', 'shopify:block:select']) {
      document.addEventListener(name, (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.sectionId === SECTION_ID) openDrawer();
      });
    }
    document.addEventListener('shopify:section:deselect', (event) => {
      if (/** @type {CustomEvent} */ (event).detail?.sectionId === SECTION_ID) closeDrawer();
    });
  }

  if (config.iconFont && document.fonts?.load) {
    const warm = () => document.fonts.load('24px "Material Symbols Outlined"', 'local_shipping').catch(() => {});
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
    else setTimeout(warm, 2000);
  }

  installFetchSync();

  /* 16/10 (piloto glod-v2, adversarial): o Horizon decide o rodapé fixo (cart-summary-sticky) uma vez, na abertura/atualização,
     quando a gaveta ainda não tem a altura final (animação, imagens, nossos extras) — ficava 'false' com o rodapé em 38% e o
     botão de checkout abaixo da dobra (a original tem o rodapé fixo). Refaz a MESMA conta dele (rodapé ≤ 50% = fixo) sempre que
     o tamanho muda; não força fixo quando o rodapé é grande demais (celular com muitos extras). */
  const recomputeSticky = () => {
    const dialog = drawer()?.querySelector('dialog');
    if (!dialog) return;
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!summary || !dialog.querySelector('.cart-drawer__content')) return;
    const altura = dialog.getBoundingClientRect().height;
    if (!altura) return;
    dialog.setAttribute('cart-summary-sticky', summary.getBoundingClientRect().height / altura > 0.5 ? 'false' : 'true');
  };
  const agendarSticky = () => {
    for (const ms of [60, 350, 900, 1800]) setTimeout(recomputeSticky, ms);
  };
  document.addEventListener(DRAWER_OPEN_EVENT, (event) => {
    if (event.target instanceof HTMLElement && event.target.id === DRAWER_ID) agendarSticky();
  });
  document.addEventListener(StandardEvents.cartLinesUpdate, agendarSticky);
  window.addEventListener('resize', () => setTimeout(recomputeSticky, 120));
  if ('ResizeObserver' in window) {
    const alvo = drawer()?.querySelector('dialog');
    if (alvo) new ResizeObserver(() => recomputeSticky()).observe(alvo);
  }

  /** API pública para o molde KB e apps (W1c): ver docs/waves/20261015-2159-horizon-piloto/gaveta_horizon.md */
  /** @type {any} */ (window).SbotCart = Object.freeze({ add: addToCart, refresh, open: openDrawer, close: closeDrawer });
}
